import { teamsAuth } from './teams-auth';

/**
 * Client-side access-context resolution for usage telemetry: *where* the app runs (host surface) and
 * *how* (OS + form factor). The tokens produced here mirror the backend `UsageHostSurfaces` /
 * `UsagePlatforms` / `UsageFormFactors` enums so the ingest beacon can bucket sessions without any
 * server-side re-parsing. Everything is best-effort and dependency-free — unresolved facets fall back to
 * `unknown`, and nothing throws into the caller.
 */

export type UsageHostSurface = 'teams' | 'outlook' | 'm365' | 'web' | 'unknown';
export type UsagePlatform = 'windows' | 'macos' | 'linux' | 'ios' | 'android' | 'unknown';
export type UsageFormFactor = 'desktop' | 'mobile' | 'unknown';

export type UsageContext = {
  host: UsageHostSurface;
  os: UsagePlatform;
  formFactor: UsageFormFactor;
};

/** Minimal shape of the UA-Client-Hints API we consume (not in the ambient DOM lib types everywhere). */
type UserAgentData = {
  platform?: string;
  mobile?: boolean;
};

/**
 * Maps the Teams SDK launch host to a host surface. The M365 app reports its host as `Office`. When the
 * SDK gave us no host name but other signals say we are inside Teams, treat it as Teams; otherwise the app
 * is running as a standalone browser tab (`web`).
 */
export function classifyHost(hostName: string | null | undefined, isInTeams: boolean): UsageHostSurface {
  switch (hostName?.trim().toLowerCase()) {
    case 'teams':
      return 'teams';
    case 'outlook':
      return 'outlook';
    case 'office':
    case 'm365':
      return 'm365';
    default:
      return isInTeams ? 'teams' : 'web';
  }
}

/**
 * Resolves the OS from the UA-CH platform (preferred, when the browser exposes it) or the user-agent
 * string. Order matters: Android and iOS are checked before Linux/macOS because their UA strings also
 * contain "Linux"/"like Mac".
 */
export function classifyOs(userAgent: string | null | undefined, uaPlatform?: string | null): UsagePlatform {
  const hint = uaPlatform?.trim().toLowerCase();
  switch (hint) {
    case 'windows':
      return 'windows';
    case 'macos':
      return 'macos';
    case 'android':
      return 'android';
    case 'ios':
      return 'ios';
    case 'linux':
    case 'chrome os':
    case 'chromium os':
      return 'linux';
    default:
      break;
  }

  const ua = (userAgent ?? '').toLowerCase();
  if (!ua) return 'unknown';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/windows/.test(ua)) return 'windows';
  if (/macintosh|mac os x/.test(ua)) return 'macos';
  if (/linux|cros/.test(ua)) return 'linux';
  return 'unknown';
}

/**
 * Desktop vs mobile. Prefers the authoritative UA-CH `mobile` boolean and the resolved OS, then falls back
 * to user-agent tokens. Returns `unknown` only when there is no user-agent to read at all.
 */
export function classifyFormFactor(
  userAgent: string | null | undefined,
  os: UsagePlatform,
  options: { uaMobile?: boolean | undefined; hostClientType?: string | null } = {},
): UsageFormFactor {
  const { uaMobile, hostClientType } = options;
  if (uaMobile === true) return 'mobile';
  if (os === 'ios' || os === 'android') return 'mobile';
  if (hostClientType === 'android' || hostClientType === 'ios') return 'mobile';

  const ua = (userAgent ?? '').toLowerCase();
  if (!ua) return 'unknown';
  if (/mobile|iphone|ipod|android|windows phone/.test(ua)) return 'mobile';
  return 'desktop';
}

function getUserAgentData(): UserAgentData | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
}

/**
 * Resolves the full session context from the current environment: host from the Teams SDK state (already
 * initialized at app start), OS/form-factor from the user agent. Safe to call before Teams init completes —
 * it simply resolves `web` until the host context lands. Never throws.
 */
export function resolveUsageContext(): UsageContext {
  let hostName: string | null = null;
  let isInTeams = false;
  let hostClientType: string | null = null;
  try {
    const state = teamsAuth.getContextState();
    hostName = state.hostName;
    isInTeams = state.isInTeams;
    hostClientType = state.hostClientType;
  } catch {
    // Teams state unavailable (e.g. tests) — fall through to browser defaults.
  }

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
  const uaData = getUserAgentData();

  const os = classifyOs(userAgent, uaData?.platform);
  return {
    host: classifyHost(hostName, isInTeams),
    os,
    formFactor: classifyFormFactor(userAgent, os, { uaMobile: uaData?.mobile, hostClientType }),
  };
}
